import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthStore } from '@auth/authStore';
import { useUrlAssetScope, useRegisterFilters, buildScopedSearch } from '@hooks';
import { FilterBar, TablePagination } from '@components';
import RegisterTable from '@/shared/components/RegisterTable/RegisterTable';
import {
  buildOverview, locationOverviewFilters, componentOverviewFilters,
  overviewSearchText, overviewColumns, sortOverview,
} from '@/entities/overview/overview.model';
import { useOverviewResources } from './useOverviewResources';
import styles from './OperationalOverview.module.css';

const sourceNames = { sites: 'Site catalogue', assets: 'Asset catalogue', components: 'Component catalogue', anomalies: 'Anomaly records' };
const countLabel = value => value === null || value === undefined ? '—' : value.toLocaleString();
const readyRows = source => source.isLoading || source.error ? null : source.data?.rows ?? null;

function OverviewContent({ companyId }) {
  const { siteId, assetId } = useUrlAssetScope();
  const navigate = useNavigate();
  const sources = useOverviewResources(companyId);
  // Loading or failed sources are unknown, not a successful empty list.
  const sites = readyRows(sources.sites), assets = readyRows(sources.assets);
  const components = readyRows(sources.components), anomalies = readyRows(sources.anomalies);
  const model = useMemo(() => buildOverview({ sites, assets, components, anomalies }, siteId, assetId),
    [sites, assets, components, anomalies, siteId, assetId]);
  const columns = useMemo(() => overviewColumns(model.level), [model.level]);
  const namespace = 'overview.' + model.level;
  const filters = useRegisterFilters({
    namespace, items: model.rows,
    definitions: model.level === 'components' ? componentOverviewFilters : locationOverviewFilters,
    getSearchText: overviewSearchText,
  });
  const [sort, setSort] = useState({ key: 'label', direction: 'asc' });
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ key: '', page: 0 });
  const pageKey = JSON.stringify([companyId, siteId, assetId, filters.query, filters.values, sort, rowsPerPage]);
  const activeSort = columns.some(column => column.key === sort.key) ? sort : { key: 'label', direction: 'asc' };
  const sorted = sortOverview(filters.filtered, activeSort, columns);
  const lastPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
  const page = pagination.key === pageKey ? Math.min(pagination.page, lastPage) : 0;
  const visible = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const primary = sources[model.level];
  const title = assetId ? model.assetLabel : siteId ? model.siteLabel : 'Fleet overview';

  function open(path, scope = { siteId, assetId }, extra = {}) {
    const query = buildScopedSearch('', scope, extra);
    navigate({ pathname: path, search: query ? '?' + query : '' });
  }
  function drill(row) {
    if (model.level === 'components') {
      open('/iod', { siteId: row.siteId, assetId: row.assetId }, { 'components.selected': row.id });
    } else {
      open('/home', { siteId: row.siteId, assetId: model.level === 'assets' ? row.assetId : null }, { homeView: 'overview' });
    }
  }
  function renderCell(row, column) {
    if (column.key === 'label') return <>{row.label}<small>{row.description || 'No description recorded'}</small></>;
    if (column.key === 'health') return <span className={styles.health} data-tone={row.tone}>{row.health}</span>;
    if (column.key === 'subsystem') return <span className={styles.subsystem}>{row.subsystem}</span>;
    if (column.key === 'technique') return <span className={styles.techniques}>{row.techniques.join(' · ') || 'None confirmed'}</span>;
    if (['open', 'closed', 'unknown'].includes(column.key)) {
      if (!row.stats) return <span title={row.ambiguous ? 'RDS code is missing or matches multiple components.' : 'Anomaly source unavailable.'}>
        {row.ambiguous ? 'Unresolved' : '—'}
      </span>;
      const value = row.stats[column.key];
      return <span className={styles.count} data-open={column.key === 'open' && value > 0}>{countLabel(value)}</span>;
    }
    const value = column.value(row);
    return column.numeric ? countLabel(value) : value || 'Not recorded';
  }

  return <div className={styles.overview}>
    <nav className={styles.hierarchy} aria-label="Overview hierarchy">
      <button type="button" aria-current={!siteId && !assetId ? 'page' : undefined}
        onClick={() => open('/home', { siteId: null, assetId: null }, { homeView: 'overview' })}>Fleet</button>
      {siteId && <><span aria-hidden="true">/</span><button type="button" aria-current={!assetId ? 'page' : undefined}
        onClick={() => open('/home', { siteId, assetId: null }, { homeView: 'overview' })}>{model.siteLabel}</button></>}
      {assetId && <><span aria-hidden="true">/</span><span aria-current="page">{model.assetLabel}</span></>}
    </nav>

    <header className={styles.header}>
      <div><span className={styles.eyebrow}>{assetId ? 'ASSET' : siteId ? 'SITE' : 'FLEET'} OVERVIEW</span>
        <h2>{title}</h2><p>{model.level === 'components' ? 'Select a component to open its IOD detail.' :
          'Select a ' + (model.level === 'sites' ? 'site' : 'asset') + ' to explore the next level.'}</p></div>
      <div className={styles.actions}>
        <button type="button" disabled={Boolean(model.invalidScope)} onClick={() => open('/home', { siteId, assetId },
          { homeView: 'map', mapMode: siteId || assetId ? 'assets' : 'sites' })}>View map</button>
        <button type="button" disabled={Boolean(model.invalidScope)} onClick={() => open('/anomaly-suite', { siteId, assetId },
          { anomalyView: 'register' })}>View anomaly register</button>
        <button type="button" disabled={Boolean(model.invalidScope)} onClick={() => open('/inspection-suite')}>Inspection workspace</button>
      </div>
    </header>

    {Object.entries(sources).map(([name, source]) => source.error ? <div className={styles.notice} key={name}>
      <p role="alert">{sourceNames[name]}: {source.error}</p>
      <button type="button" className={styles.link} onClick={() => source.reload()}>Retry {sourceNames[name].toLowerCase()}</button>
    </div> : source.data?.excluded > 0 && <p key={name} className={styles.notice}>
      {sourceNames[name]}: {source.data.excluded} record(s) with a different company ID were excluded.
    </p>)}

    {model.invalidScope ? <p role="alert" className={styles.notice}>{model.invalidScope} Use the scope selector or return to Fleet.</p> : <>
      <div className={styles.metrics}>
        <article><span>Registered {model.level}</span><strong>{countLabel(model.childCount)}</strong><small>Immediate children in this scope</small></article>
        <article><span>Open anomaly records</span><strong>{countLabel(model.totals?.open)}</strong><small>From the returned anomaly list</small></article>
        <article><span>Other / unknown status</span><strong>{countLabel(model.totals?.unknown)}</strong><small>Not treated as closed</small></article>
        <article className={styles.pending}><span>Inspection coverage & overdue</span><strong>Not connected</strong><small>Awaiting inspection data and evidence links</small></article>
      </div>
      <p className={styles.explanation}>
        Summaries describe the whole location scope; table search and filters affect rows only.
        {' '}{countLabel(model.totals?.total)} anomaly records in scope · {countLabel(model.totals?.closed)} recorded closed.
        {' '}“—” means loading or unavailable. Zero open records is not an integrity assessment.
      </p>
      {model.unassigned > 0 && <p className={styles.notice}>
        {model.unassigned} anomaly record(s) in these totals cannot be assigned to a unique {model.level === 'sites' ? 'site' : model.level === 'assets' ? 'asset' : 'component'} row.
        Missing, unknown or ambiguous location/component IDs are not guessed.
        {' '}The anomaly register remains available for review.
      </p>}
      {(model.unlistedAssets > 0 || model.unlistedComponents > 0) && <p className={styles.notice}>
        Catalogue links need review:
        {model.unlistedAssets > 0 ? ' ' + model.unlistedAssets + ' assets have no matching registered site.' : ''}
        {model.unlistedComponents > 0 ? ' ' + model.unlistedComponents + ' components have no matching registered site/asset.' : ''}
        {' '}Their rows cannot be reached through a valid parent. The original IOD remains the catalogue review surface.
      </p>}
      <p className={styles.explanation}>Recorded health and last-inspection values come directly from the catalogue.
        {model.level === 'components' ? ' Techniques describe eligibility, not assigned or completed work.' : ''}
        {' '}Anomaly counts use open_closed; all other status values stay unknown.
        {siteId ? ' Anomalies without this exact site/asset scope are excluded here; review Fleet for unassigned records.' : ''}
      </p>
      <section className={styles.register} aria-label={'Overview ' + model.level}>
        {primary.isLoading ? <p className={styles.feedback} role="status">Loading {model.level}…</p> :
          primary.error ? <p className={styles.feedback}>The {model.level} list is unavailable. Use its retry button above.</p> : <>
            <FilterBar filters={filters} noun={model.level} />
            <div className={styles.tableArea}>
              {visible.length ? <RegisterTable rows={visible} columns={columns} sort={activeSort} onSortChange={setSort}
                getRowKey={row => row.id} onOpen={drill} renderCell={renderCell}
                caption={'Registered ' + model.level + ' and recorded anomaly counts'} /> :
                <div className={styles.feedback}><p>{model.rows.length ? 'No rows match these filters.' : 'No ' + model.level + ' are registered in this scope.'}</p>
                  {model.rows.length > 0 && <button type="button" className={styles.link} onClick={filters.clear}>Clear filters</button>}</div>}
            </div>
            <TablePagination count={sorted.length} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_event, next) => setPagination({ key: pageKey, page: next })}
              onRowsPerPageChange={event => setRowsPerPage(Number(event.target.value))}
              rowsPerPageOptions={[25, 50, 100]} />
          </>}
      </section>
    </>}
  </div>;
}

export default function OperationalOverview() {
  const companyId = useAuthStore(state => state.companyID);
  return companyId ? <OverviewContent key={String(companyId)} companyId={companyId} /> :
    <p className={styles.feedback}>Select a company to view its overview.</p>;
}